import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { login } from '../../store/authSlice';
import { AppDispatch, RootState } from '../../store';
import { LoginRequest } from '../../types';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import TwoFactorChallenge from './TwoFactorChallenge';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required')
});

const formatCountdown = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
};

const LoginForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error, rateLimitRetryAfter } = useSelector((state: RootState) => state.auth);

  const [countdown, setCountdown] = useState<number>(0);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  // H11 / F7 — when the server returns requires2FA, swap the form for the
  // challenge component until either the code is verified or the user backs out.
  const [twoFactor, setTwoFactor] = useState<{ challengeToken: string; email: string } | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginRequest>({
    resolver: yupResolver(schema)
  });

  // Start countdown when rateLimitRetryAfter changes
  useEffect(() => {
    if (rateLimitRetryAfter && rateLimitRetryAfter > 0) {
      setCountdown(rateLimitRetryAfter);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [rateLimitRetryAfter]);

  const isRateLimited = countdown > 0;

  const onSubmit = async (data: LoginRequest) => {
    const result = await dispatch(login(data));
    if (login.fulfilled.match(result)) {
      const payload = result.payload as any;
      if (payload?.requires2FA && payload?.challengeToken) {
        // H11 / F7 — swap the form for the 2FA challenge step. The auth
        // slice did not set credentials because the payload had no user
        // (server only returns challengeToken in this branch).
        setTwoFactor({ challengeToken: payload.challengeToken, email: data.email });
        return;
      }
      navigate('/dashboard');
    }
  };

  // H11 / F7 — when the email+password step returned requires2FA, show
  // the challenge form instead of the credential form. Cancelling returns
  // the user to the sign-in screen (they can try a different account).
  if (twoFactor) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <TwoFactorChallenge
            challengeToken={twoFactor.challengeToken}
            email={twoFactor.email}
            onCancel={() => setTwoFactor(null)}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Trading Dashboard
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
            Sign in to continue
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              fullWidth
              label="Email Address"
              autoComplete="email"
              autoFocus
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={isRateLimited}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              disabled={isRateLimited}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(p => !p)} edge="end" size="small" tabIndex={-1}>
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {isRateLimited ? (
              <Alert
                severity="warning"
                icon={<LockClockOutlinedIcon />}
                sx={{ mt: 2 }}
              >
                <strong>Too many failed attempts.</strong>
                <br />
                You can try again in <strong>{formatCountdown(countdown)}</strong>
              </Alert>
            ) : error ? (
              <Alert
                severity={
                  error.includes('pending') ? 'warning' :
                  error.includes('suspended') ? 'error' :
                  error.includes('expired') ? 'warning' : 'error'
                }
                sx={{ mt: 2 }}
              >
                {error}
              </Alert>
            ) : null}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || isRateLimited}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isRateLimited ? (
                `Locked — retry in ${formatCountdown(countdown)}`
              ) : (
                'Sign In'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Typography variant="body2">
                <Link
                  to="#"
                  onClick={(e) => { e.preventDefault(); setForgotOpen(true); }}
                  style={{ textDecoration: 'none' }}
                >
                  Forgot password?
                </Link>
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                Don't have an account?{' '}
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
        <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
      </Box>
    </Container>
  );
};

export default LoginForm;
